import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { publicAxios as axios } from "../config/axios";
import EtaAlertModal from "../components/EtaAlertModal";
import GaraazDatePickerModalETA, {
  isEnquiryRootOrder,
} from "../views/Orders/components/GaraazDatePickerModalETA";
import { getStandardDeliverySlotPickerProps } from "../views/Orders/utils/garaazSlotPickerUtils";
import services from "../views/Orders/services";
import { minusFiveMinutesFromCurrentTime } from "../utils/dateResolvers";
import {
  ETA_HARD_LOCK_EVENT,
  type EtaHardLockEventDetail,
} from "../utils/etaHardLock";
import endpoints from "../utils/endpoints";
import { useUserDetailsContext } from "./userDetailsContext";
import { useGetAllSuppliers } from "../views/Orders/queryHooks";

export type EtaAlertType = "soft" | "hard";

export type EtaAlertState = {
  alertType: EtaAlertType;
  orderId: string;
  orderNo?: string;
  eta?: string;
} | null;

type EtaAlertContextValue = {
  alert: EtaAlertState;
  openSoftAlert: (payload: Omit<NonNullable<EtaAlertState>, "alertType">) => void;
  openHardAlert: (payload: Omit<NonNullable<EtaAlertState>, "alertType">) => void;
  closeAlert: () => void;
  /** Hard lock with real order page visible (view-only) */
  isHardDetailsViewing: boolean;
};

const EtaAlertContext = createContext<EtaAlertContextValue | undefined>(
  undefined,
);

export function useEtaAlert(): EtaAlertContextValue {
  const ctx = useContext(EtaAlertContext);
  if (!ctx) {
    throw new Error("useEtaAlert must be used within EtaAlertProvider");
  }
  return ctx;
}

/**
 * Optional hook for listeners that may mount before the provider (should not happen
 * when nested correctly). Returns null instead of throwing.
 */
export function useEtaAlertOptional(): EtaAlertContextValue | null {
  return useContext(EtaAlertContext) ?? null;
}

type Props = {
  children: React.ReactNode;
};

type HardLockApiItem = {
  orderId: string;
  orderNo?: string;
  eta?: string;
};

/**
 * Soft is dismissible. Hard requires focus on that enquiry/order:
 * PE/OH may act only on the hard-locked job until ETA is extended;
 * everything else is blocked (UI + API).
 */
export const EtaAlertProvider: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const userDetails = useUserDetailsContext();
  const { data: allSuppliers } = useGetAllSuppliers();
  const [alert, setAlert] = useState<EtaAlertState>(null);
  const [isSubmittingEta, setIsSubmittingEta] = useState(false);
  const [extendIsEnquiry, setExtendIsEnquiry] = useState(false);
  const [extendSlotPicker, setExtendSlotPicker] = useState({
    useGaraazSlots: false,
    garaazSlots: [] as ReturnType<
      typeof getStandardDeliverySlotPickerProps
    >["garaazSlots"],
  });
  const [isResolvingExtendOrder, setIsResolvingExtendOrder] = useState(false);
  const [hardDetailsOpen, setHardDetailsOpen] = useState(false);
  const [pendingHardLocks, setPendingHardLocks] = useState<HardLockApiItem[]>(
    [],
  );

  const {
    isOpen: isExtendOpen,
    onOpen: onExtendOpen,
    onClose: onExtendClose,
  } = useDisclosure();

  const isHardDetailsViewing =
    alert?.alertType === "hard" && hardDetailsOpen;

  useEffect(() => {
    if (!isExtendOpen) {
      setExtendIsEnquiry(false);
      setIsResolvingExtendOrder(false);
      setExtendSlotPicker({ useGaraazSlots: false, garaazSlots: [] });
      return;
    }
    const orderId = alert?.orderId;
    if (!orderId) {
      setExtendIsEnquiry(false);
      setIsResolvingExtendOrder(false);
      setExtendSlotPicker({ useGaraazSlots: false, garaazSlots: [] });
      return;
    }

    let cancelled = false;
    setIsResolvingExtendOrder(true);
    (async () => {
      try {
        const order = await services.getSingleOrder(orderId);
        if (!cancelled) {
          setExtendIsEnquiry(isEnquiryRootOrder(order));
          setExtendSlotPicker(
            getStandardDeliverySlotPickerProps(order, allSuppliers),
          );
        }
      } catch (error) {
        console.error("Failed to resolve enquiry ETA for extend modal", error);
        if (!cancelled) {
          setExtendIsEnquiry(false);
          setExtendSlotPicker({ useGaraazSlots: false, garaazSlots: [] });
        }
      } finally {
        if (!cancelled) {
          setIsResolvingExtendOrder(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isExtendOpen, alert?.orderId, allSuppliers]);

  // Keep reminder banner on the locked order page; leaving /orders restores modal
  useEffect(() => {
    if (!isHardDetailsViewing) {
      return;
    }
    const onOrderDetailsPage = /^\/orders\/[^/]+/.test(location.pathname);
    if (!onOrderDetailsPage) {
      setHardDetailsOpen(false);
    }
  }, [isHardDetailsViewing, location.pathname]);

  const closeAlert = useCallback(() => {
    setAlert(null);
    setHardDetailsOpen(false);
  }, []);

  const openSoftAlert = useCallback(
    (payload: Omit<NonNullable<EtaAlertState>, "alertType">) => {
      setHardDetailsOpen(false);
      setAlert((current) => {
        if (current?.alertType === "hard") {
          return current;
        }
        if (
          current?.alertType === "soft" &&
          current.orderId === payload.orderId
        ) {
          return current;
        }
        return { ...payload, alertType: "soft" };
      });
    },
    [],
  );

  const openHardAlert = useCallback(
    (payload: Omit<NonNullable<EtaAlertState>, "alertType">) => {
      setAlert((current) => {
        if (
          current?.alertType === "hard" &&
          current.orderId === payload.orderId
        ) {
          return current;
        }
        return { ...payload, alertType: "hard" };
      });
      setHardDetailsOpen(false);
    },
    [],
  );

  const showNextPendingHardLock = useCallback(
    (locks: HardLockApiItem[]) => {
      if (!locks.length) {
        return;
      }
      const [next, ...rest] = locks;
      setPendingHardLocks(rest);
      openHardAlert({
        orderId: next.orderId,
        orderNo: next.orderNo,
        eta: next.eta,
      });
    },
    [openHardAlert],
  );

  useEffect(() => {
    const userId = userDetails?._id;
    const authToken = localStorage.getItem("auth_orca");
    if (!userId || !authToken) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await axios.get<{ locks?: HardLockApiItem[] }>(
          endpoints.getMyEtaHardLocks,
          {
            headers: {
              Authorization: authToken,
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
            baseURL: process.env.REACT_APP_BASE_URL,
            params: { _ts: Date.now() },
          },
        );
        if (cancelled) {
          return;
        }
        const locks = Array.isArray(response.data?.locks)
          ? response.data.locks.filter((lock) => Boolean(lock?.orderId))
          : [];
        if (locks.length) {
          showNextPendingHardLock(locks);
        } else {
          console.debug("ETA Hard locks on load: none", {
            status: response.status,
          });
        }
      } catch (error) {
        console.error("Failed to fetch ETA Hard locks on load", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userDetails?._id, showNextPendingHardLock]);

  useEffect(() => {
    const onHardLock = (event: Event) => {
      const detail = (event as CustomEvent<EtaHardLockEventDetail>).detail;
      if (!detail?.orderId) {
        return;
      }
      openHardAlert({
        orderId: detail.orderId,
        orderNo: detail.orderNo,
        eta: detail.eta,
      });
    };

    window.addEventListener(ETA_HARD_LOCK_EVENT, onHardLock);
    return () => {
      window.removeEventListener(ETA_HARD_LOCK_EVENT, onHardLock);
    };
  }, [openHardAlert]);

  const handleExtendEtaSubmit = useCallback(
    async (
      etaGivenTime: string,
      eta: string,
      reason: string,
      _supplierId?: string,
      _onClose?: () => void,
      slotPayload?: Record<string, unknown> | null,
    ) => {
      if (!alert?.orderId && !alert?.orderNo) {
        return;
      }
      setIsSubmittingEta(true);
      try {
        let targetOrderId = alert.orderId;
        const looksLikeMongoId = /^[a-f\d]{24}$/i.test(
          String(targetOrderId || ""),
        );
        if (!looksLikeMongoId) {
          const orderNo = alert.orderNo || alert.orderId;
          const response = await services.getAllGaraazOrdersV2(
            `&orderNo=${orderNo}&limit=1&includeTestOrders=true`,
          );
          const matched = response?.orders?.[0];
          if (!matched?._id) {
            throw new Error(`Could not resolve order id for ${orderNo}`);
          }
          targetOrderId = String(matched._id);
        }

        const result = await services.setOrderETA(targetOrderId, {
          etaGivenTime:
            etaGivenTime ||
            (typeof minusFiveMinutesFromCurrentTime === "function"
              ? minusFiveMinutesFromCurrentTime()
              : minusFiveMinutesFromCurrentTime),
          eta,
          reason,
          ...(slotPayload ?? {}),
        });
        if (!result) {
          throw new Error("Failed to update ETA");
        }
        closeAlert();
        if (pendingHardLocks.length) {
          showNextPendingHardLock(pendingHardLocks);
        }
      } catch (error: any) {
        console.error("Failed to extend ETA from alert modal", error);
        toast({
          title: "Could not extend ETA",
          description:
            error?.response?.data?.errorMsg ||
            error?.message ||
            "Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        throw error;
      } finally {
        setIsSubmittingEta(false);
      }
    },
    [
      alert?.orderId,
      alert?.orderNo,
      closeAlert,
      pendingHardLocks,
      showNextPendingHardLock,
      toast,
    ],
  );

  const handleViewDetails = useCallback(async () => {
    let orderNo = alert?.orderNo;
    if (!orderNo && alert?.orderId) {
      const looksLikeMongoId = /^[a-f\d]{24}$/i.test(String(alert.orderId));
      if (!looksLikeMongoId) {
        orderNo = alert.orderId;
      } else {
        try {
          const order = await services.getSingleOrder(alert.orderId);
          orderNo = order?.orderNo ? String(order.orderNo) : undefined;
        } catch (error) {
          console.error("Failed to resolve orderNo for ETA alert details", error);
        }
      }
    }
    if (!orderNo) {
      toast({
        title: "Cannot open details",
        description: "Order number is missing for this alert.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (alert?.alertType === "soft") {
      closeAlert();
      navigate(`/orders/${orderNo}`);
      return;
    }
    // Hard: open the locked enquiry/order (only this page stays actionable)
    setHardDetailsOpen(true);
    navigate(`/orders/${orderNo}`);
  }, [alert?.alertType, alert?.orderId, alert?.orderNo, closeAlert, navigate, toast]);

  const contextValue = useMemo(
    () => ({
      alert,
      openSoftAlert,
      openHardAlert,
      closeAlert,
      isHardDetailsViewing: Boolean(isHardDetailsViewing),
    }),
    [alert, openSoftAlert, openHardAlert, closeAlert, isHardDetailsViewing],
  );

  const orderEtasForModal = alert?.eta
    ? [{ etaGivenTime: alert.eta, eta: alert.eta, reason: "" }]
    : [];

  const hardPresentation = isHardDetailsViewing ? "locked-details" : "modal";
  const showAlertChrome = Boolean(alert);

  return (
    <EtaAlertContext.Provider value={contextValue}>
      {children}

      {showAlertChrome && (
        <EtaAlertModal
          isOpen={showAlertChrome}
          alertType={alert!.alertType}
          presentation={hardPresentation}
          orderNo={alert!.orderNo}
          eta={alert!.eta}
          onDismiss={alert!.alertType === "soft" ? closeAlert : undefined}
          onExtendEta={onExtendOpen}
          onViewDetails={
            alert!.orderNo || alert!.orderId ? handleViewDetails : undefined
          }
          onBackToLock={
            alert!.alertType === "hard"
              ? () => setHardDetailsOpen(false)
              : undefined
          }
          isExtendLoading={isSubmittingEta}
        />
      )}

      {(alert?.orderId || alert?.orderNo) && (
        <GaraazDatePickerModalETA
          isOpen={isExtendOpen}
          onClose={onExtendClose}
          orderETAs={orderEtasForModal}
          modifiedOrderETAs={[]}
          modelHeader={
            alert.orderNo
              ? `Extend ETA — ${alert.orderNo}`
              : "Extend ETA"
          }
          okBtnText="Save ETA"
          handleSubmit={handleExtendEtaSubmit}
          isEnquiryOrder={extendIsEnquiry}
          isEnquiryResolutionPending={isResolvingExtendOrder}
          useGaraazSlots={extendSlotPicker.useGaraazSlots}
          garaazSlots={extendSlotPicker.garaazSlots}
        />
      )}
    </EtaAlertContext.Provider>
  );
};

export default EtaAlertProvider;
